import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import { useSSEStore } from '../../stores/sseStore';
import {
  MessageCircle, Search, Send, Sparkles, Trash2, Ban,
  ExternalLink, Clock, ShoppingCart, Tag, Loader2, X,
  AlertCircle, ChevronDown, ChevronUp, Edit3, GraduationCap
} from 'lucide-react';
import { QuestionCoachDrawer } from './QuestionCoachDrawer';
import { QuestionProductPanel, type ProductCandidate } from './QuestionProductPanel';
import { secureUrl } from '../../utils/url';
import EditMLTab from '../catalog/tabs/EditMLTab';
import { Can, useCan } from '../../lib/rbac';

// ── Types ──
interface BuyerOrder {
  ml_order_id: number;
  status: string;
  paid_amount: number;
  buyer_nickname: string;
  title: string;
  unit_price: number;
  quantity: number;
  currency_id: string;
  ship_status: string | null;
  ship_tracking: string | null;
  date_created: string;
}

interface EnrichedQuestion {
  id: number;
  date_created: string;
  item_id: string;
  marketplace_item_id: string;
  status: string;
  text: string;
  text_translated?: string;
  site_id: string;
  from: { id: number; nickname?: string; answered_questions?: number };
  item_title: string | null;
  item_price: number | null;
  item_net_proceeds: number | null;
  item_currency: string | null;
  item_permalink: string | null;
  item_thumbnail: string | null;
  item_status: string | null;
  item_condition: string | null;
  item_available_qty: number | null;
  asin: string | null;
  amazon_cost: number | null;
  amazon_brand: string | null;
  amazon_category: string | null;
  amazon_rating: number | null;
  amazon_ratings_total: number | null;
  amazon_model: string | null;
  amazon_color: string | null;
  amazon_size: string | null;
  amazon_weight: number | null;
  amazon_dimensions: string | null;
  amazon_description: string | null;
  amazon_features: string[];
  matched_directives: string[];
  buyer_question_count: number;
  buyer_order: BuyerOrder | null;
  ai_draft?: string | null;
  ai_draft_status?: string | null;
  /** Conversación previa de este comprador en ESTE artículo (hilo real de ML que vio el asistente) */
  buyer_thread?: { date: string | null; question: string; answer: string | null }[];
  /** Producto por el que pregunta el comprador (propuesto por el asistente; confirma el operador) */
  product_candidates?: ProductCandidate[];
  product_confirmed_asin?: string | null;
}

interface AnsweredQuestion {
  id: number;
  text: string;
  text_translated?: string;
  date_created: string;
  item_id: string;
  answer?: { text: string; status: string; date_created: string };
}

const SITE_FLAGS: Record<string, string> = {
  MLA: '🇦🇷', MLM: '🇲🇽', MLC: '🇨🇱', MCO: '🇨🇴', MLU: '🇺🇾', MLB: '🇧🇷', MLV: '🇻🇪', MPE: '🇵🇪', MEC: '🇪🇨',
};

function timeAgo(dateStr: string): { text: string; urgency: 'red' | 'yellow' | 'blue' } {
  const diff = Date.now() - new Date(dateStr).getTime();
  const hours = diff / (1000 * 60 * 60);
  const urgency = hours > 24 ? 'red' : hours > 4 ? 'yellow' : 'blue';

  if (hours < 1) {
    const mins = Math.floor(diff / (1000 * 60));
    return { text: `${mins}m`, urgency };
  }
  if (hours < 24) return { text: `${Math.floor(hours)}h`, urgency };
  const days = Math.floor(hours / 24);
  return { text: `${days}d`, urgency };
}

// Publicación no activa: Mercado Libre NO permite responder ("Item must be active").
// La tarjeta cambia de color y avisa; el botón Responder queda deshabilitado.
function listingState(status: string | null): { kind: 'paused' | 'closed' | 'other'; label: string } | null {
  if (!status || status === 'active') return null;
  if (status === 'paused') return { kind: 'paused', label: 'Publicación PAUSADA — Mercado Libre no permite responder hasta reactivarla' };
  if (status === 'closed') return { kind: 'closed', label: 'Publicación CERRADA — Mercado Libre no permite responder' };
  return { kind: 'other', label: `Publicación en estado "${status}" — Mercado Libre solo permite responder con la publicación activa` };
}

const listingCardClass: Record<'paused' | 'closed' | 'other', string> = {
  paused: 'bg-amber-500/10 border-amber-500/50',
  closed: 'bg-red-500/10 border-red-500/50',
  other: 'bg-amber-500/10 border-amber-500/50',
};
const listingBannerClass: Record<'paused' | 'closed' | 'other', string> = {
  paused: 'bg-amber-500/20 text-amber-300 border-amber-500/40',
  closed: 'bg-red-500/20 text-red-300 border-red-500/40',
  other: 'bg-amber-500/20 text-amber-300 border-amber-500/40',
};

const urgencyColors = {
  red: 'bg-red-500/20 text-red-400 border-red-500/30',
  yellow: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  blue: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
};

export default function QuestionsTab({ sellerId }: { sellerId: number }) {
  const { t } = useTranslation();
  const [questions, setQuestions] = useState<EnrichedQuestion[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAnswered, setShowAnswered] = useState(false);
  const [answered, setAnswered] = useState<AnsweredQuestion[]>([]);
  const [answeredLoading, setAnsweredLoading] = useState(false);
  const refreshTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  const loadQuestions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<EnrichedQuestion[]>(`/mercadolibre/questions/pending?seller_id=${sellerId}`);
      if (res.success && res.data) {
        setQuestions(res.data);
        setTotal((res as any).meta?.total || res.data.length);
      } else {
        setError((res as any).error || t('ml.q.loadError'));
      }
    } catch (err) {
      setError(t('ml.q.loadError'));
    }
    setLoading(false);
  }, [sellerId, t]);

  useEffect(() => { loadQuestions(); }, [loadQuestions]);

  // Auto-refresh every 60s
  useEffect(() => {
    refreshTimer.current = setInterval(() => {
      loadQuestions();
    }, 60000);
    return () => { if (refreshTimer.current) clearInterval(refreshTimer.current); };
  }, [loadQuestions]);

  // SSE: refresh on question events
  const { subscribe } = useSSEStore();
  const loadRef = useRef(loadQuestions);
  loadRef.current = loadQuestions;
  useEffect(() => {
    const refresh = () => loadRef.current();
    const unsubs = [
      subscribe('question:answered', refresh),
      subscribe('question:blocked', refresh),
      // El asistente terminó el borrador → la tarjeta muestra "Respuesta lista" sin esperar 60s
      subscribe('question:draft_ready', refresh),
    ];
    return () => unsubs.forEach(u => u());
  }, [subscribe]);

  const handleAnswered = (questionId: number) => {
    setQuestions(prev => prev.filter(q => q.id !== questionId));
    setTotal(prev => Math.max(0, prev - 1));
  };

  const handleDeleted = (questionId: number) => {
    setQuestions(prev => prev.filter(q => q.id !== questionId));
    setTotal(prev => Math.max(0, prev - 1));
  };

  const handleBlocked = (buyerId: number) => {
    setQuestions(prev => prev.filter(q => q.from?.id !== buyerId));
  };

  const handleSkipped = (questionId: number) => {
    setQuestions(prev => prev.filter(q => q.id !== questionId));
  };

  const loadAnswered = async () => {
    setAnsweredLoading(true);
    try {
      const res = await api.get<{ questions: AnsweredQuestion[] }>(
        `/mercadolibre/questions?seller_id=${sellerId}&status=ANSWERED&limit=20`
      );
      if (res.success && res.data) {
        const qs = (res.data as any).questions || res.data;
        setAnswered(Array.isArray(qs) ? qs : []);
      }
    } catch { /* non-fatal */ }
    setAnsweredLoading(false);
  };

  return (
    <div className="space-y-4">
      {/* Header stats */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="bg-[var(--color-bg-secondary)] rounded-lg px-4 py-2 border border-[var(--color-border)]">
            <span className="text-sm text-[var(--color-text-muted)]">{t('ml.q.pending')}: </span>
            <span className="text-lg font-bold text-[var(--color-text)]">{total}</span>
          </div>
          {questions.length > 0 && (() => {
            const avgMs = questions.reduce((acc, q) => acc + (Date.now() - new Date(q.date_created).getTime()), 0) / questions.length;
            const avgH = Math.floor(avgMs / (1000 * 60 * 60));
            const avgM = Math.floor((avgMs % (1000 * 60 * 60)) / (1000 * 60));
            return (
              <div className="bg-[var(--color-bg-secondary)] rounded-lg px-4 py-2 border border-[var(--color-border)]">
                <span className="text-sm text-[var(--color-text-muted)]">{t('ml.q.avgWait')}: </span>
                <span className="text-lg font-bold text-[var(--color-text)]">{avgH}h {avgM}m</span>
              </div>
            );
          })()}
        </div>
        <button
          onClick={loadQuestions}
          disabled={loading}
          className="px-3 py-2 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-lg text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text)] disabled:opacity-50"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t('ml.q.refresh')}
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && !questions.length ? (
        <div className="p-8 text-center text-[var(--color-text-muted)]">
          <Loader2 className="w-8 h-8 mx-auto mb-3 animate-spin opacity-50" />
          {t('common.loading')}
        </div>
      ) : questions.length === 0 && !loading ? (
        <div className="bg-[var(--color-bg-secondary)] rounded-lg border border-[var(--color-border)] p-8 text-center">
          <MessageCircle className="w-12 h-12 mx-auto mb-3 text-[var(--color-text-muted)] opacity-30" />
          <p className="text-[var(--color-text-muted)]">{t('ml.q.noQuestions')}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {questions.map(q => (
            <QuestionCard
              key={q.id}
              question={q}
              sellerId={sellerId}
              allQuestions={questions}
              onAnswered={handleAnswered}
              onDeleted={handleDeleted}
              onBlocked={handleBlocked}
              onSkipped={handleSkipped}
            />
          ))}
        </div>
      )}

      {/* Answered section */}
      <div className="pt-2">
        <button
          onClick={() => { setShowAnswered(!showAnswered); if (!showAnswered) loadAnswered(); }}
          className="flex items-center gap-1.5 text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
        >
          {showAnswered ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          {t('ml.q.answered')} ({answered.length})
        </button>
        {showAnswered && (
          <div className="mt-3 space-y-2">
            {answeredLoading ? (
              <div className="text-center py-4"><Loader2 className="w-5 h-5 mx-auto animate-spin opacity-50" /></div>
            ) : answered.length === 0 ? (
              <p className="text-xs text-[var(--color-text-muted)] py-2">{t('ml.q.noAnswered')}</p>
            ) : answered.map(q => (
              <div key={q.id} className="bg-[var(--color-bg-secondary)] rounded-lg p-3 text-xs border border-[var(--color-border)]">
                <p className="text-[var(--color-text-muted)]">Q: &ldquo;{(q as { text_es?: string }).text_es || q.text_translated || q.text}&rdquo;</p>
                {q.answer && <p className="text-green-400 mt-1">A: {q.answer.text}</p>}
                <p className="text-[var(--color-text-muted)] opacity-50 text-[10px] mt-1">{new Date(q.date_created).toLocaleDateString()}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════
// QUESTION CARD
// ════════════════════════════════════════════════════
function QuestionCard({
  question: q,
  sellerId,
  allQuestions,
  onAnswered,
  onDeleted,
  onBlocked,
  onSkipped,
}: {
  question: EnrichedQuestion;
  sellerId: number;
  allQuestions: EnrichedQuestion[];
  onAnswered: (id: number) => void;
  onDeleted: (id: number) => void;
  onBlocked: (buyerId: number) => void;
  onSkipped: (id: number) => void;
}) {
  const { t, i18n } = useTranslation();
  const can = useCan();
  const [answerText, setAnswerText] = useState('');
  const [answerTextPt, setAnswerTextPt] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [answerLoading, setAnswerLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [blockLoading, setBlockLoading] = useState(false);
  const [showFeatures, setShowFeatures] = useState(false);
  const [showOrderPopup, setShowOrderPopup] = useState(false);
  const [showOtherBuyerQs, setShowOtherBuyerQs] = useState(false);
  const [cardError, setCardError] = useState<string | null>(null);
  const [showEditDrawer, setShowEditDrawer] = useState(false);
  const [showCoach, setShowCoach] = useState(false);
  // Producto confirmado por el operador (se sincroniza con lo que trae el servidor)
  const [confirmedAsin, setConfirmedAsin] = useState<string | null>(q.product_confirmed_asin || null);
  useEffect(() => { setConfirmedAsin(q.product_confirmed_asin || null); }, [q.product_confirmed_asin]);
  const needsProductConfirm = !!q.product_candidates?.length && !confirmedAsin;

  const ta = timeAgo(q.date_created);
  const isBrazil = q.site_id === 'MLB';

  // Generate AI draft
  const handleGenerateAI = async () => {
    setAiLoading(true);
    setCardError(null);
    try {
      const res = await api.post<{ ai_draft: string | null; ai_draft_pt: string | null; status?: string }>(
        `/mercadolibre/questions/${q.id}/generate-ai?seller_id=${sellerId}`, {}
      );
      if (res.success && res.data?.ai_draft) {
        setAnswerText(res.data.ai_draft);
        if (res.data.ai_draft_pt) setAnswerTextPt(res.data.ai_draft_pt);
      } else if ((res as any).error === 'agent_busy') {
        setCardError('El asistente está generando la respuesta. Esperá unos segundos y volvé a tocar Generar AI.');
      } else {
        setCardError((res as any).error || t('ml.q.aiError'));
      }
    } catch {
      setCardError(t('ml.q.aiError'));
    }
    setAiLoading(false);
  };

  // Answer
  const handleAnswer = async () => {
    const text = isBrazil && answerTextPt ? answerTextPt : answerText;
    if (!text || text.length < 2) return;
    setAnswerLoading(true);
    setCardError(null);
    try {
      const body: Record<string, string> = { text };
      if (isBrazil && answerTextPt && answerText) {
        // Send Spanish as text, Portuguese as text_translated for ML cross-border translation
        body.text = answerText;
        body.text_translated = answerTextPt;
      }
      const res = await api.post(`/mercadolibre/questions/${q.id}/answer?seller_id=${sellerId}`, body);
      if (res.success) {
        onAnswered(q.id);
      } else if ((res as any).code === 'already_answered') {
        // ML ya tiene una respuesta para esta pregunta: no hay nada que enviar.
        setCardError((res as any).error);
        setTimeout(() => onAnswered(q.id), 2500);
      } else {
        setCardError((res as any).error || t('ml.q.answerError'));
      }
    } catch {
      setCardError('No se pudo enviar (falló la conexión con el servidor). Esperá unos segundos y volvé a intentar.');
    }
    setAnswerLoading(false);
  };

  // Delete
  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      const res = await api.delete(`/mercadolibre/questions/${q.id}?seller_id=${sellerId}`);
      if (res.success) onDeleted(q.id);
      else setCardError((res as any).error || t('ml.q.deleteError'));
    } catch {
      setCardError(t('ml.q.deleteError'));
    }
    setDeleteLoading(false);
  };

  // Block
  const handleBlock = async () => {
    setBlockLoading(true);
    try {
      const res = await api.post(`/mercadolibre/questions/block-user?seller_id=${sellerId}`, {
        buyer_id: q.from.id,
        question_ids: [q.id],
      });
      if (res.success) onBlocked(q.from.id);
      else setCardError((res as any).error || t('ml.q.blockError'));
    } catch {
      setCardError(t('ml.q.blockError'));
    }
    setBlockLoading(false);
  };

  const features = q.amazon_features || [];
  const listing = listingState(q.item_status);
  const cardClass = listing
    ? listingCardClass[listing.kind]
    : 'bg-[var(--color-bg-secondary)] border-[var(--color-border)]';

  return (
    <div className={`rounded-lg border p-4 space-y-3 ${cardClass}`}>
      {listing && (
        <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium ${listingBannerClass[listing.kind]}`}>
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {listing.label}
        </div>
      )}
      {/* ── Row 1: Product info + question ── */}
      <div className="flex items-start gap-3">
        {/* Thumbnail */}
        {q.item_thumbnail && (
          <img src={secureUrl(q.item_thumbnail)} alt="" className="w-14 h-14 rounded-lg object-cover bg-white flex-shrink-0" />
        )}

        <div className="flex-1 min-w-0">
          {/* Title + badges */}
          <div className="flex items-start gap-2 flex-wrap mb-1">
            <span className="text-sm text-[var(--color-text)] font-medium leading-tight">{q.item_title || q.item_id}</span>
            {q.asin && (
              <a href={`https://www.amazon.com/dp/${q.asin}`} target="_blank" rel="noopener noreferrer"
                className="text-[10px] font-mono bg-orange-500/15 text-orange-400 px-1.5 py-0.5 rounded flex-shrink-0 no-underline hover:bg-orange-500/25">{q.asin}</a>
            )}
            {q.item_permalink ? (
              <a href={q.item_permalink} target="_blank" rel="noopener noreferrer"
                className="text-[10px] font-mono bg-blue-500/15 text-blue-400 px-1.5 py-0.5 rounded flex-shrink-0 no-underline hover:bg-blue-500/25">
                {SITE_FLAGS[q.site_id] || ''} {q.site_id}: {q.marketplace_item_id || q.item_id}
              </a>
            ) : (
              <span className="text-sm flex-shrink-0">{SITE_FLAGS[q.site_id] || q.site_id}</span>
            )}
            {q.item_condition && (
              <span className="text-[10px] bg-[var(--color-bg)] text-[var(--color-text-muted)] px-1.5 py-0.5 rounded border border-[var(--color-border)]">{q.item_condition}</span>
            )}
          </div>

          {/* Pricing row — solo con la llave de dinero */}
          <Can cap="global.view_money">
          <div className="flex items-center gap-3 text-xs text-[var(--color-text-muted)] mb-2 flex-wrap">
            {q.item_price != null && (
              <span>ML: <strong className="text-[var(--color-text)]">${q.item_price.toFixed(2)} {q.item_currency || ''}</strong></span>
            )}
            {q.item_net_proceeds != null && (
              <span>Net: <strong className="text-green-400">${q.item_net_proceeds.toFixed(2)}</strong></span>
            )}
            {q.amazon_cost != null && (
              <span>AMZ: <strong>${q.amazon_cost.toFixed(2)}</strong></span>
            )}
            {q.item_net_proceeds != null && q.amazon_cost != null && (() => {
              const profit = q.item_net_proceeds! - q.amazon_cost!;
              return (
                <span>Profit: <strong className={profit >= 0 ? 'text-green-400' : 'text-red-400'}>${profit.toFixed(2)}</strong></span>
              );
            })()}
          </div>
          </Can>

          {/* Amazon specs inline */}
          {(q.amazon_brand || q.amazon_model || q.amazon_color || q.amazon_rating) && (
            <div className="flex items-center gap-3 text-xs text-[var(--color-text-muted)] mb-2 flex-wrap">
              {q.amazon_brand && <span>{q.amazon_brand}</span>}
              {q.amazon_model && <span>{q.amazon_model}</span>}
              {q.amazon_color && <span>{q.amazon_color}</span>}
              {q.amazon_rating != null && (
                <span className="text-yellow-400">{'★'.repeat(Math.round(q.amazon_rating))} {q.amazon_rating}</span>
              )}
            </div>
          )}

          {/* Weight / dimensions / size */}
          {(q.amazon_weight || q.amazon_dimensions || q.amazon_size) && (
            <div className="flex items-center gap-3 text-xs text-[var(--color-text-muted)] mb-2 flex-wrap">
              {q.amazon_weight && <span>{q.amazon_weight}kg</span>}
              {q.amazon_dimensions && <span>{q.amazon_dimensions}</span>}
              {q.amazon_size && <span>Size: {q.amazon_size}</span>}
            </div>
          )}

          {/* Features (expandable) */}
          {features.length > 0 && (
            <div className="mb-2">
              <button
                onClick={() => setShowFeatures(!showFeatures)}
                className="flex items-center gap-1 text-xs text-[var(--color-primary)] hover:underline"
              >
                {showFeatures ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                {t('ml.q.features')} ({features.length})
              </button>
              {showFeatures && (
                <ul className="mt-1 space-y-0.5 text-xs text-[var(--color-text-muted)] pl-3">
                  {features.map((f, i) => (
                    <li key={i} className="list-disc">{f}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        {/* Right side: time badge + date */}
        <div className="flex flex-col items-end gap-1 flex-shrink-0">
          <span className={`text-xs px-2 py-1 rounded border ${urgencyColors[ta.urgency]}`}>
            <Clock className="w-3 h-3 inline mr-1" />{ta.text}
          </span>
          <span className="text-[10px] text-[var(--color-text-muted)]">{new Date(q.date_created).toLocaleString()}</span>
        </div>
      </div>

      {/* ── Row 2: Question text (swap primary/secondary based on UI language) ── */}
      <div className="bg-[var(--color-bg)] rounded-lg p-3 border border-[var(--color-border)]">
        {(() => {
          const lang = i18n.language;
          const hasTranslation = q.text_translated && q.text_translated !== q.text;
          // When UI=es: primary is Spanish — text_es (traducción nuestra, preguntas
          // MLB en portugués) > text_translated (original, ya español en MLA/MLM) > text.
          // Secondary keeps the buyer's original so the operator can verify.
          const textEs = (q as { text_es?: string }).text_es;
          const primaryText = lang === 'es'
            ? (textEs || (hasTranslation ? q.text_translated! : q.text))
            : q.text;
          const secondaryText = lang === 'es'
            ? (textEs && hasTranslation ? q.text_translated! : (hasTranslation ? q.text : null))
            : (hasTranslation ? q.text_translated! : null);
          return (
            <>
              <p className="text-sm text-[var(--color-text)] leading-relaxed">{primaryText}</p>
              {secondaryText && (
                <p className="text-xs text-[var(--color-text-muted)] mt-1.5 italic border-t border-[var(--color-border)] pt-1.5">{secondaryText}</p>
              )}
            </>
          );
        })()}
      </div>

      {/* ── Row 3: Buyer info + directives ── */}
      <div className="flex items-center gap-2 flex-wrap text-xs">
        {/* Buyer */}
        <span className="text-[var(--color-text-muted)]">{q.from?.nickname || `#${q.from?.id}`}</span>

        {q.buyer_question_count > 1 && (
          <span className="bg-purple-500/15 text-purple-400 px-1.5 py-0.5 rounded">
            x{q.buyer_question_count}
          </span>
        )}

        {/* Buyer order badge */}
        {q.buyer_order && (
          <span className="relative">
            <button
              onClick={() => setShowOrderPopup(!showOrderPopup)}
              className="flex items-center gap-1 bg-green-500/15 text-green-400 px-2 py-0.5 rounded hover:bg-green-500/25"
            >
              <ShoppingCart className="w-3 h-3" />
              {t('ml.q.order')} #{q.buyer_order.ml_order_id}
            </button>
            {showOrderPopup && (
              <OrderPopup order={q.buyer_order} onClose={() => setShowOrderPopup(false)} />
            )}
          </span>
        )}

        {/* Directive badges */}
        {q.matched_directives.map(dk => (
          <span key={dk} className="bg-violet-500/15 text-violet-400 px-2 py-0.5 rounded flex items-center gap-1">
            <Tag className="w-3 h-3" />{dk}
          </span>
        ))}
      </div>

      {/* Other questions from same buyer (expandable) */}
      {q.buyer_question_count > 1 && (() => {
        const otherQs = allQuestions.filter(oq => oq.id !== q.id && oq.from?.id === q.from?.id);
        if (!otherQs.length) return null;
        return (
          <div>
            <button
              onClick={() => setShowOtherBuyerQs(!showOtherBuyerQs)}
              className="flex items-center gap-1 text-xs text-amber-400 hover:text-amber-300"
            >
              {showOtherBuyerQs ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              {t('ml.q.otherBuyerQs')} ({otherQs.length})
            </button>
            {showOtherBuyerQs && (
              <div className="mt-1.5 space-y-1.5 pl-3 border-l-2 border-amber-500/30">
                {otherQs.map(oq => (
                  <div key={oq.id} className="text-xs">
                    <span className="text-[var(--color-text-muted)]">{oq.item_title ? oq.item_title.slice(0, 50) + '...' : oq.item_id}</span>
                    {' · '}
                    <span className="text-[var(--color-text)]">&ldquo;{(oq.text_translated || oq.text).slice(0, 120)}{(oq.text_translated || oq.text).length > 120 ? '...' : ''}&rdquo;</span>
                    {' · '}
                    <span className="text-[var(--color-text-muted)] opacity-60">{timeAgo(oq.date_created).text}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {/* Conversación anterior con este comprador en ESTE artículo (hilo de ML guardado
          junto al borrador: es lo mismo que vio el asistente al responder) */}
      {!!q.buyer_thread?.length && (
        <div className="rounded-lg border border-sky-500/25 bg-sky-500/5 px-3 py-2">
          <div className="mb-1.5 flex items-center gap-1 text-[10px] font-medium uppercase tracking-wider text-sky-400">
            <MessageCircle className="w-3 h-3" />
            Conversación anterior con este comprador en este artículo ({q.buyer_thread.length})
          </div>
          <div className="space-y-2">
            {q.buyer_thread.map((m, i) => (
              <div key={i} className="text-xs">
                <div className="text-[var(--color-text)] break-words">
                  <span className="text-[var(--color-text-muted)]">Comprador: </span>
                  &ldquo;{m.question}&rdquo;
                  {m.date && (
                    <span className="text-[var(--color-text-muted)] opacity-60"> · {timeAgo(m.date).text}</span>
                  )}
                </div>
                <div className="mt-0.5 break-words pl-3 text-[var(--color-text-muted)]">
                  {m.answer ? <>Respuesta: &ldquo;{m.answer}&rdquo;</> : <span className="italic">Sin responder cuando llegó esta pregunta</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Producto por el que pregunta (variante u otro): lo propone el asistente, confirma el operador */}
      {!!q.product_candidates?.length && (
        <QuestionProductPanel
          questionId={q.id}
          siteId={q.site_id}
          sellerId={sellerId}
          candidates={q.product_candidates}
          confirmedAsin={confirmedAsin}
          onConfirmed={(asin) => setConfirmedAsin(asin)}
        />
      )}

      {/* ── Row 4: Answer editor ── */}
      <Can cap="questions.answer">
      <div className="space-y-2">
        {isBrazil && <div className="text-[10px] font-medium uppercase tracking-wider text-[var(--color-text-muted)]">ES (review)</div>}
        <textarea
          value={answerText}
          onChange={(e) => setAnswerText(e.target.value)}
          placeholder={t('ml.q.answerPlaceholder')}
          rows={2}
          maxLength={2000}
          className="w-full px-3 py-2 bg-[var(--color-bg)] border border-[var(--color-border)] rounded-lg text-sm text-[var(--color-text)] resize-y placeholder:text-[var(--color-text-muted)] focus:outline-none focus:border-[var(--color-primary)]"
        />
        {answerText && (
          <div className="text-xs text-[var(--color-text-muted)] text-right">{answerText.length}/2000</div>
        )}

        {/* PT textarea for Brazil */}
        {isBrazil && (
          <>
            <div className="text-[10px] font-medium uppercase tracking-wider text-green-400">PT (will be sent)</div>
            <textarea
              value={answerTextPt}
              onChange={(e) => setAnswerTextPt(e.target.value)}
              placeholder={t('ml.q.answerPlaceholderPt')}
              rows={2}
              maxLength={2000}
              className="w-full px-3 py-2 bg-[var(--color-bg)] border border-green-500/20 rounded-lg text-sm text-[var(--color-text)] resize-y placeholder:text-[var(--color-text-muted)] focus:outline-none focus:border-green-500"
            />
          </>
        )}
      </div>
      </Can>

      {/* ── Row 5: Error ── */}
      {cardError && (
        <div className="flex items-center gap-2 text-xs text-red-400">
          <AlertCircle className="w-3 h-3" />
          {cardError}
        </div>
      )}

      {/* ── Respuesta lista (borrador IA pre-generado) ──
             Llena el editor de respuesta: exige las dos llaves. */}
      {q.ai_draft && can('questions.ai_draft') && can('questions.answer') && (
        <button
          type="button"
          onClick={() => { setAnswerText(q.ai_draft || ''); if (isBrazil) setAnswerTextPt(q.ai_draft || ''); }}
          title="Clic para usar esta respuesta"
          className="w-full text-left flex items-start gap-2 px-3 py-2 bg-violet-500/10 border border-violet-500/25 rounded-lg text-sm text-[var(--color-text)] hover:bg-violet-500/20"
        >
          <Sparkles className="w-4 h-4 text-violet-400 shrink-0 mt-0.5" />
          <span className="min-w-0">
            <span className="block text-[10px] font-medium uppercase tracking-wider text-violet-400">Respuesta lista</span>
            <span className="block whitespace-pre-wrap break-words">{q.ai_draft}</span>
          </span>
        </button>
      )}

      {/* ── Row 6: Action buttons ── */}
      <div className="flex items-center gap-2 flex-wrap">
        <Can cap="questions.ai_draft">
        <button
          onClick={handleGenerateAI}
          disabled={aiLoading}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-violet-500/15 text-violet-400 rounded-lg text-sm hover:bg-violet-500/25 disabled:opacity-50"
        >
          {aiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          {t('ml.q.generateAI')}
        </button>
        </Can>

        <Can cap="questions.answer">
        <button
          onClick={handleAnswer}
          disabled={answerLoading || (!answerText && !answerTextPt) || !!listing || needsProductConfirm}
          title={listing ? listing.label : needsProductConfirm ? 'Confirmá el producto por el que pregunta el comprador' : undefined}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-green-500/15 text-green-400 rounded-lg text-sm hover:bg-green-500/25 disabled:opacity-50"
        >
          {answerLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          {t('ml.q.answer')}
        </button>
        </Can>

        <Can cap="catalog.edit_listing">
        <button
          onClick={() => setShowEditDrawer(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-500/15 text-blue-400 rounded-lg text-sm hover:bg-blue-500/25"
          title="Editar listing CBT"
        >
          <Edit3 className="w-4 h-4" />
          Editar listing
        </button>
        </Can>

        <Can cap="questions.ai_draft">
        <button
          onClick={() => setShowCoach(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-500/15 text-amber-500 rounded-lg text-sm hover:bg-amber-500/25"
          title="Charlar con la AI sobre esta pregunta y enseñarle reglas para el futuro"
        >
          <GraduationCap className="w-4 h-4" />
          Entrenar AI
        </button>
        </Can>

        <Can cap="questions.delete">
        <button
          onClick={() => onSkipped(q.id)}
          className="px-3 py-1.5 bg-[var(--color-bg)] text-[var(--color-text-muted)] rounded-lg text-sm hover:text-[var(--color-text)] border border-[var(--color-border)]"
        >
          {t('ml.q.skip')}
        </button>
        </Can>

        <div className="ml-auto flex items-center gap-2">
          <Can cap="questions.delete">
          <button
            onClick={handleDelete}
            disabled={deleteLoading}
            className="flex items-center gap-1.5 px-3 py-1.5 text-[var(--color-text-muted)] hover:text-red-400 rounded-lg text-sm hover:bg-red-500/10 disabled:opacity-50"
            title={t('common.delete')}
          >
            {deleteLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
          </button>
          </Can>

          <Can cap="questions.block_user">
          <button
            onClick={handleBlock}
            disabled={blockLoading}
            className="flex items-center gap-1.5 px-3 py-1.5 text-[var(--color-text-muted)] hover:text-red-400 rounded-lg text-sm hover:bg-red-500/10 disabled:opacity-50"
            title={t('ml.q.blockUser')}
          >
            {blockLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Ban className="w-3.5 h-3.5" />}
          </button>
          </Can>
        </div>
      </div>

      {showEditDrawer && (
        <EditListingDrawer
          itemId={q.item_id}
          sellerId={sellerId}
          title={q.item_title}
          thumbnail={q.item_thumbnail}
          onClose={() => setShowEditDrawer(false)}
        />
      )}

      {showCoach && (
        <QuestionCoachDrawer
          sellerId={sellerId}
          questionId={q.id}
          questionText={(q as { text_es?: string }).text_es || q.text_translated || q.text}
          itemTitle={q.item_title || ''}
          itemId={q.item_id || ''}
          siteId={q.site_id || ''}
          aiDraft={answerText || q.ai_draft || null}
          onUseAnswer={(text) => {
            setAnswerText(text);
            if (isBrazil) setAnswerTextPt(text);
            setShowCoach(false);
          }}
          onClose={() => setShowCoach(false)}
        />
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════
// EDIT LISTING DRAWER (reutiliza EditMLTab de Catalog)
// ════════════════════════════════════════════════════
function EditListingDrawer({
  itemId,
  sellerId,
  title,
  thumbnail,
  onClose,
}: {
  itemId: string;
  sellerId: number;
  title: string | null;
  thumbnail: string | null;
  onClose: () => void;
}) {
  const [product, setProduct] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadProduct = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<any>(`/catalog/products/${itemId}?seller_id=${sellerId}`);
      if (res.success && res.data) {
        setProduct(res.data);
      } else {
        setError((res as any).error || 'No se pudo cargar el producto');
      }
    } catch {
      setError('Error de conexión al cargar el producto');
    }
    setLoading(false);
  }, [itemId, sellerId]);

  useEffect(() => { loadProduct(); }, [loadProduct]);

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose} />
      <div className="fixed top-0 right-0 h-full w-full max-w-2xl bg-[var(--color-surface)] shadow-2xl z-50 overflow-y-auto animate-slide-in">
        <div className="sticky top-0 bg-[var(--color-surface)] border-b border-[var(--color-border)] px-5 py-3 flex items-center justify-between z-10">
          <div className="flex items-center gap-3 min-w-0">
            {thumbnail && (
              <img src={secureUrl(thumbnail)} alt="" className="w-10 h-10 rounded object-contain bg-white flex-shrink-0" />
            )}
            <div className="min-w-0">
              <h2 className="text-sm font-semibold text-[var(--color-text)] truncate">{title || itemId}</h2>
              <p className="text-xs text-[var(--color-text-muted)] font-mono">{itemId}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-[var(--color-surface-hover)] flex-shrink-0">
            <X size={20} />
          </button>
        </div>

        <div className="p-5">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-[var(--color-primary)]" />
            </div>
          ) : error ? (
            <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          ) : product ? (
            <EditMLTab product={product} sellerId={sellerId} onRefresh={loadProduct} />
          ) : null}
        </div>
      </div>
    </>
  );
}

// ════════════════════════════════════════════════════
// ORDER POPUP
// ════════════════════════════════════════════════════
function OrderPopup({ order, onClose }: { order: BuyerOrder; onClose: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="absolute z-50 top-8 left-0 w-72 bg-[var(--color-bg)] border border-[var(--color-border)] rounded-lg shadow-xl p-3 space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-[var(--color-text)]">{t('ml.q.orderDetail')}</span>
        <button onClick={onClose} className="text-[var(--color-text-muted)] hover:text-[var(--color-text)]">
          <X className="w-3 h-3" />
        </button>
      </div>
      <div className="space-y-1 text-xs">
        <div className="flex justify-between">
          <span className="text-[var(--color-text-muted)]">{t('sales.orders')}</span>
          <span className="text-[var(--color-text)] font-mono">#{order.ml_order_id}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-[var(--color-text-muted)]">{t('common.status')}</span>
          <span className="text-[var(--color-text)]">{order.status}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-[var(--color-text-muted)]">{t('common.total')}</span>
          <span className="text-[var(--color-text)] font-medium">{order.currency_id} ${order.paid_amount}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-[var(--color-text-muted)]">{t('catalog.product')}</span>
          <span className="text-[var(--color-text)] truncate ml-2">{order.title}</span>
        </div>
        {order.ship_status && (
          <div className="flex justify-between">
            <span className="text-[var(--color-text-muted)]">{t('sales.shipmentStatus')}</span>
            <span className="text-[var(--color-text)]">{order.ship_status}</span>
          </div>
        )}
        {order.ship_tracking && (
          <div className="flex justify-between">
            <span className="text-[var(--color-text-muted)]">Tracking</span>
            <span className="text-[var(--color-text)] font-mono">{order.ship_tracking}</span>
          </div>
        )}
        <div className="flex justify-between">
          <span className="text-[var(--color-text-muted)]">{t('common.date')}</span>
          <span className="text-[var(--color-text)]">{new Date(order.date_created).toLocaleDateString()}</span>
        </div>
      </div>
    </div>
  );
}
